import React, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { useLocation, Redirect } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Stack, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { InputAdornment, makeStyles } from '@material-ui/core';
import { keys } from 'lodash';
import http from '../../../config/http';
import { AUTH_UPDATE_MOBILE } from '../../../config/api';
import { _route } from '../../../utils';
import PhoneNumber from '../../../components/common/PhoneNumber';
import checkCircle from '../../../assets/images/check-circle.svg';
import { useDispatch, useSelector } from 'react-redux';
import { setUserInfoToRedux } from '../../../components/auth/action';

const useStyles = makeStyles(() => ({
  textField: {
    padding: '0px!important',
    fontSize: '16px!important',
    fontFamily: 'Inter !important',
    fontWeight: '400',
    marginTop: '8px!important',
    transition: '.3s',
    borderRadius: '8px!important',
    '& .MuiInputLabel-formControl': {
      fontSize: '14px',
      lineHeight: '1em',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      // border: '1px solid #5433FF',
    },
    '& .MuiFormHelperText-root': {
      letterSpacing: '0 !important',
      fontFamily: 'Inter',
      margin: '6px 0 0 0 !important',
      fontWeight: '500',
    },
    '& .MuiOutlinedInput-root .MuiInputBase-input': {
      borderRadius: '8px',
      background: '#fff',
      fontSize: '14px !important',
      // border: '1px solid #D0D5DD',
      boxShadow: ' 0px 1px 2px rgb(16 24 40 / 5%)',
      padding: '13.5px 14px',
      transition: '.3s',
      '&:focus': {
        transition: '.3s',
        outline: '0',
        // boxShadow: 'inset 0 1px 1px rgb(0 0 0 / 8%), 0 0 8px rgb(102 175 233 / 60%)',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderWidth: '1px !important',
    },
  },
  phoneNumberClass: {
    padding: '0px!important',
    fontSize: '16px!important',
    fontFamily: 'Inter !important',
    fontWeight: '400',
    marginTop: '0px!important',
    transition: '.3s',
    borderRadius: '8px!important',
  },
  borderClass: {
    '&.react-tel-input .form-control': {
      borderRadius: '8px',
    },
  },
  buttonClass: {
    '&.flag-dropdown': {
      borderTopLeftRadius: '8px',
      borderBottomLeftRadius: '8px',
      background: '#fff',
    },
  },
}));

const buttonStyles = {
  borderRadius: '10px',
  boxShadow: '0 4px 11px rgb(0 0 0 / 11%)',
  width: '100%',
  padding: '10px 14px',
  color: '#fff',
  transition: '0.2s',
  marginTop: '5px',
  fontWeight: '500',
  backgroundColor: '#5433ff',
  // borderColor: '#5433ff',
  // border: '1px solid #5433ff',
  fontFamily: 'Inter',
  letterSpacing: '0',
  textTransform: 'inherit',
  '&:hover': {
    backgroundColor: '#5433FF',
    borderColor: '#5433FF',
    opacity: '0.9',
    boxShadow: '0 4px 11px rgb(0 0 0 / 11%)',
  },
  '&:focus': {
    backgroundColor: '#5433FF',
    borderColor: '#5433FF',
    opacity: '0.9',
    boxShadow: '0 4px 11px rgb(0 0 0 / 11%)',
  },
  '&:visited': {
    backgroundColor: '#5433FF',
    borderColor: '#5433FF',
    opacity: '0.9',
    boxShadow: '0 4px 11px rgb(0 0 0 / 11%)',
  },
};

export default function SocialAuthSetPhoneForm({ handleSetupFinish }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { user } = useSelector(({ auth }) => auth);
  const [isValidMobile, setIsValidMobile] = useState(false);
  const [mobile, setMobile] = useState({ number: user.mobile, dialCode: user.countryCode ?? '+91' });
  const routeParams = useLocation().search;
  const searchParamsData = new URLSearchParams(routeParams);
  const next = searchParamsData.get('next');

  useEffect(() => {}, [user]);

  const RegisterSchema = Yup.object().shape({
    // email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    brandName: Yup.string()
      .required('Brand Name is required')
      .min(2, 'Must be more than 2 characters')
      .max(100, 'Must be less than 100 characters'),
    password: Yup.string().min(8).required('Password is required'),
  });

  const formik = useFormik({
    initialValues: {
      email: user?.email || '',
      brandName: '',
    },
    validateOnMount: true,
    validationSchema: RegisterSchema,
    onSubmit: async (values, { setSubmitting }) => {
      const searchParamsData = new URLSearchParams(routeParams);
      const source = { autopilot: searchParamsData.get('autopilot') || false };
      if (!keys(source).length === 0 || !keys(source).includes('autopilot')) {
        if (document.referrer.indexOf('autopilot') > -1) {
          source.utm_source = 'autopilot';
        }
      }

      const payload = {
        // since it's social auth verified user, we don't have to send email
        mobile: mobile.number,
        countryCode: mobile.dialCode,
        password: values.password,
        brandName: values.brandName,
      };

      try {
        await http.post(AUTH_UPDATE_MOBILE, payload);
        handleSetupFinish(true);
        await setUserInfoToRedux(dispatch);
        if (window.dataLayer) {
          window.dataLayer.push([
            'event',
            'conversion',
            {
              send_to: '10925068683',
              value: 'TaLQCJvQhYgZEIujvdko',
            },
          ]);
        }

        return;
      } catch (err) {
        toast.error(err.response.data.message);
      }
      setSubmitting(false);
    },
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps, isValid } = formik;

  if (user.isSetPassword) {
    return <Redirect to={next ?? _route('stores')} />;
  }

  return (
    <>
      {user ? (
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack spacing={3} style={{ textAlign: 'left' }}>
              <div className="customLabel">Email</div>
              <TextField
                disabled
                fullWidth
                type="email"
                {...getFieldProps('email')}
                error={Boolean(touched.email && errors.email)}
                helperText={touched.email && errors.email}
                InputLabelProps={{ shrink: false }}
                className={classes.textField}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <img src={checkCircle} />
                    </InputAdornment>
                  ),
                }}
              />
              {!user.mobile && <div className="dividerPrimary"></div>}
              <div className="customLabel mgTop">Phone number *</div>
              <div className={classes.phoneNumberClass}>
                <PhoneNumber
                  disabled={user.mobile}
                  mobileNumber={mobile.number}
                  countryCode={mobile.dialCode}
                  handleDialCode={(val) => setMobile({ ...mobile, dialCode: val })}
                  handlePhoneNumber={(number, isValid) => {
                    setMobile({ ...mobile, number: number });
                    setIsValidMobile(isValid);
                  }}
                />
              </div>
              {user.mobile && <div className="dividerPrimary"></div>}
              <div className="customLabel">Brand Name</div>
              <TextField
                autoFocus
                fullWidth
                type="text"
                placeholder="Enter brand name here..."
                {...getFieldProps('brandName')}
                error={Boolean(touched.brandName && errors.brandName)}
                helperText={touched.brandName && errors.brandName}
                InputLabelProps={{ shrink: false }}
                className={classes.textField}
                InputProps={{
                  endAdornment:
                    errors.brandName === undefined ? (
                      <InputAdornment position="end">
                        <img src={checkCircle} />
                      </InputAdornment>
                    ) : (
                      ''
                    ),
                }}
              />

              <div className="customLabel">Set Password</div>
              <TextField
                fullWidth
                type="password"
                placeholder="Enter new password here..."
                {...getFieldProps('password')}
                error={Boolean(touched.password && errors.password)}
                helperText={touched.password && errors.password}
                InputLabelProps={{ shrink: false }}
                className={classes.textField}
                InputProps={{
                  endAdornment:
                    errors.password === undefined ? (
                      <InputAdornment position="end">
                        <img src={checkCircle} />
                      </InputAdornment>
                    ) : (
                      ''
                    ),
                }}
              />

              <LoadingButton
                disabled={!(isValidMobile && isValid)}
                fullWidth
                size="large"
                type="submit"
                sx={buttonStyles}
                variant="contained"
                className="finish-signup-btn"
                loading={isSubmitting}
              >
                Finish Sign up
              </LoadingButton>
            </Stack>
          </Form>
        </FormikProvider>
      ) : (
        <>Loading...</>
      )}
    </>
  );
}
